// FILE: composerQueue.ts
// Purpose: Schemas for the server-side composer message queue (per user, per thread).
// Layer: Shared contracts (schema-only)
// Depends on: orchestration schemas for turn payloads and attachments.
//
// The composer queue lets someone write a follow-up while an agent is mid-turn without derailing
// it. Rows are owned by the author, not by the thread owner: two people working the same thread
// each see only their own queued messages.
//
// Every row carries two snapshots because formatting the outgoing prompt (effort prefix, quoted
// contexts, image-only bootstrap) lives in web-only modules:
//   - `turn`     — the final text and options, ready to become a `thread.turn.start` command.
//                  The server dispatches this verbatim and never re-formats it.
//   - `composer` — the raw composer state, so "Edit" can put the message back the way it was.

import { Schema } from "effect";

import { IsoDateTime, MessageId, ThreadId, TrimmedNonEmptyString, UserId } from "./baseSchemas";
import {
  AssistantDeliveryMode,
  ChatAttachment,
  ModelSelection,
  ProviderInteractionMode,
  ProviderStartOptions,
  ProviderKind,
  RuntimeMode,
  SourceProposedPlanReference,
  TurnDispatchMode,
  UploadChatAttachments,
} from "./orchestration";
import { ProviderMentionReference, ProviderSkillReference } from "./providerDiscovery";

export const ComposerQueuedMessageId = TrimmedNonEmptyString;
export type ComposerQueuedMessageId = typeof ComposerQueuedMessageId.Type;

export const ComposerQueueKind = Schema.Literals(["chat", "plan-follow-up"]);
export type ComposerQueueKind = typeof ComposerQueueKind.Type;

// A queued quote keeps its snapshot text for every kind: a message already committed to the queue
// must send exactly what the author saw when they queued it, even if the terminal buffer scrolled.
const ComposerQueuedTerminalQuotedContext = Schema.Struct({
  kind: Schema.Literal("terminal"),
  id: Schema.String,
  threadId: ThreadId,
  createdAt: Schema.String,
  terminalId: Schema.String,
  terminalLabel: Schema.String,
  lineStart: Schema.Number,
  lineEnd: Schema.Number,
  text: Schema.String,
});

const ComposerQueuedMessageQuotedContext = Schema.Struct({
  kind: Schema.Literal("message"),
  id: Schema.String,
  threadId: ThreadId,
  createdAt: Schema.String,
  messageId: MessageId,
  messageRole: Schema.Literals(["assistant", "user"]),
  text: Schema.String,
});

export const ComposerQueuedQuotedContext = Schema.Union([
  ComposerQueuedMessageQuotedContext,
  ComposerQueuedTerminalQuotedContext,
]);
export type ComposerQueuedQuotedContext = typeof ComposerQueuedQuotedContext.Type;

// What the server needs to build `thread.turn.start`. `text` is already formatted by the client.
export const ComposerQueuedTurnSnapshot = Schema.Struct({
  text: Schema.String,
  skills: Schema.optional(Schema.Array(ProviderSkillReference)),
  mentions: Schema.optional(Schema.Array(ProviderMentionReference)),
  modelSelection: ModelSelection,
  providerOptions: Schema.optional(ProviderStartOptions),
  assistantDeliveryMode: Schema.optional(AssistantDeliveryMode),
  runtimeMode: RuntimeMode,
  interactionMode: ProviderInteractionMode,
  sourceProposedPlan: Schema.optional(SourceProposedPlanReference),
});
export type ComposerQueuedTurnSnapshot = typeof ComposerQueuedTurnSnapshot.Type;

const ComposerQueuedChatComposerSnapshot = Schema.Struct({
  kind: Schema.Literal("chat"),
  prompt: Schema.String,
  quotedContexts: Schema.Array(ComposerQueuedQuotedContext),
  skills: Schema.Array(ProviderSkillReference),
  mentions: Schema.Array(ProviderMentionReference),
  selectedProvider: ProviderKind,
  selectedModel: Schema.NullOr(Schema.String),
  selectedPromptEffort: Schema.NullOr(Schema.String),
  envMode: Schema.Literals(["local", "worktree"]),
});

const ComposerQueuedPlanFollowUpComposerSnapshot = Schema.Struct({
  kind: Schema.Literal("plan-follow-up"),
  text: Schema.String,
  selectedProvider: ProviderKind,
  selectedModel: Schema.NullOr(Schema.String),
  selectedPromptEffort: Schema.NullOr(Schema.String),
});

export const ComposerQueuedComposerSnapshot = Schema.Union([
  ComposerQueuedChatComposerSnapshot,
  ComposerQueuedPlanFollowUpComposerSnapshot,
]);
export type ComposerQueuedComposerSnapshot = typeof ComposerQueuedComposerSnapshot.Type;

export const ComposerQueuedMessagePayload = Schema.Struct({
  previewText: Schema.String,
  turn: ComposerQueuedTurnSnapshot,
  composer: ComposerQueuedComposerSnapshot,
});
export type ComposerQueuedMessagePayload = typeof ComposerQueuedMessagePayload.Type;

export const ComposerQueuedMessage = Schema.Struct({
  queuedId: ComposerQueuedMessageId,
  threadId: ThreadId,
  // Null when the socket carries no identity (loopback legacy token, local desktop). Those rows
  // form their own queue rather than leaking into a real user's.
  userId: Schema.NullOr(UserId),
  kind: ComposerQueueKind,
  autoSend: Schema.Boolean,
  payload: ComposerQueuedMessagePayload,
  attachments: Schema.Array(ChatAttachment),
  lastError: Schema.NullOr(Schema.String),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});
export type ComposerQueuedMessage = typeof ComposerQueuedMessage.Type;

export const ComposerQueueListInput = Schema.Struct({
  threadId: ThreadId,
});
export type ComposerQueueListInput = typeof ComposerQueueListInput.Type;

export const ComposerQueueEnqueueInput = Schema.Struct({
  threadId: ThreadId,
  kind: ComposerQueueKind,
  payload: ComposerQueuedMessagePayload,
  attachments: UploadChatAttachments,
  autoSend: Schema.optional(Schema.Boolean),
});
export type ComposerQueueEnqueueInput = typeof ComposerQueueEnqueueInput.Type;

export const ComposerQueueUpdateInput = Schema.Struct({
  queuedId: ComposerQueuedMessageId,
  autoSend: Schema.Boolean,
});
export type ComposerQueueUpdateInput = typeof ComposerQueueUpdateInput.Type;

export const ComposerQueueRemoveInput = Schema.Struct({
  queuedId: ComposerQueuedMessageId,
});
export type ComposerQueueRemoveInput = typeof ComposerQueueRemoveInput.Type;

export const ComposerQueueSendInput = Schema.Struct({
  queuedId: ComposerQueuedMessageId,
  dispatchMode: Schema.optional(TurnDispatchMode),
});
export type ComposerQueueSendInput = typeof ComposerQueueSendInput.Type;

export const ComposerQueueListResult = Schema.Struct({
  threadId: ThreadId,
  items: Schema.Array(ComposerQueuedMessage),
});
export type ComposerQueueListResult = typeof ComposerQueueListResult.Type;

// `scheduled` means the thread was busy and the row was flipped to auto-send instead: the server
// will dispatch it when the live turn ends, so the caller does not have to decide.
export const ComposerQueueSendOutcome = Schema.Literals(["dispatched", "scheduled", "failed"]);
export type ComposerQueueSendOutcome = typeof ComposerQueueSendOutcome.Type;

export const ComposerQueueSendResult = Schema.Struct({
  threadId: ThreadId,
  outcome: ComposerQueueSendOutcome,
  message: Schema.optional(Schema.String),
  items: Schema.Array(ComposerQueuedMessage),
});
export type ComposerQueueSendResult = typeof ComposerQueueSendResult.Type;

export const ComposerQueueChangedPayload = Schema.Struct({
  threadId: ThreadId,
});
export type ComposerQueueChangedPayload = typeof ComposerQueueChangedPayload.Type;
